/**
 * App — root: feature check, feed load, carousel, top chrome, stats bar, dev toolbar,
 * and the window.__blitz debug API the e2e suite and the toolbar share.
 */
import { For, Show, createResource, createSignal, onCleanup, onMount } from 'solid-js';
import { Carousel, type CarouselApi } from './components/Carousel';
import { importGameFile } from './lib/zipImport';
import { captureEntry, cardFromLocation, entryContext, syncUrlToCard } from './lib/share';
import { accountPageHref } from './lib/join';
import { hasMakeDraft } from './lib/makeDraft';
import { configureUi, logUi, uiLog } from './api/ui';
import { addedCopy, benchFeed, cardBySlug, myCards, type BenchCard, type CardStatus, setCardStatus } from './api/cards';
import { mergeFresh, waitingToAdd } from './lib/mine';
import { MOCK_STATUS_LOG } from './api/mockBench';
import { stats as statsApi, type BenchSummary } from './api/stats';
import { ReviewStrip, type ReviewOutcome } from './components/ReviewStrip';
import { HIDE_REASONS, hideReason, type HideReason } from './lib/review';
import { TelemetryDisplay } from './components/TelemetryDisplay';
import { DevToolbar } from './components/DevToolbar';
import { LinkSite, type SheetMode } from './components/LinkSite';
import { CommentsSheet } from './components/CommentsSheet';
import { AccountSheet, type AccountView } from './components/AccountSheet';
import { ANONYMOUS, NO_BACKEND, account as accountApi, linkGate, type Account } from './api/account';
import { classifyMedia, uploadMedia } from './api/media';
import { likeCard, myLikes, unlikeCard } from './api/likes';
import { resetSession, supabase, supabaseConfigured } from './backend/supabase';
import { bySlug, fetchFeed } from './api/mockApi';
import type { GameMeta } from './lib/protocol';
import type { LiveStats } from './telemetry/TelemetryTypes';
import { TelemetryTransport } from './telemetry/TelemetryTransport';
import { isSharedInputAvailable } from './lib/InputPipeline';
import { measureMemory } from './lib/memory';
import { cacheStatus, registerServiceWorker, serviceWorkerActive } from './lib/ServiceWorker';

export const APP_VERSION = '0.1.0-phase1';

/** Long enough to read the sentence that says where an added card stands. */
const LONG_TOAST_MS = 6500;

/**
 * The URL fragment as it was when this module loaded. The email-confirmation link lands with
 * the session (or an error) in the fragment; supabase-js consumes and strips it asynchronously,
 * so it is captured here, synchronously, to know afterwards what kind of visit this was.
 */
const INITIAL_HASH = typeof location !== 'undefined' ? location.hash : '';

function authOutcomeFromHash(hash: string): { kind: 'confirmed' } | { kind: 'recovery' } | { kind: 'error'; message: string } | null {
  if (!hash || hash.length < 2) return null;
  const p = new URLSearchParams(hash.slice(1));
  const err = p.get('error_description') ?? p.get('error');
  if (err) return { kind: 'error', message: err.replace(/\+/g, ' ') };
  if (p.get('access_token') && p.get('type') === 'recovery') return { kind: 'recovery' };
  if (p.get('access_token') && /^(signup|email_change|magiclink)$/.test(p.get('type') ?? '')) return { kind: 'confirmed' };
  return null;
}

function userId(): string {
  try {
    const key = 'blitz.userId';
    let id = localStorage.getItem(key);
    if (!id) {
      id = 'u_' + Math.random().toString(36).slice(2, 12);
      localStorage.setItem(key, id);
    }
    return id;
  } catch {
    return 'u_anonymous';
  }
}

function supported(): string | null {
  if (typeof WebAssembly === 'undefined') return 'WebAssembly is not available in this browser.';
  if (typeof Worker === 'undefined') return 'Web Workers are not available in this browser.';
  if (!('OffscreenCanvas' in window)) return 'OffscreenCanvas is required (Chrome 69+, Firefox 105+, Safari 17+).';
  if (!('transferControlToOffscreen' in HTMLCanvasElement.prototype)) return 'canvas.transferControlToOffscreen is required.';
  return null;
}

export default function App() {
  const unsupported = supported();
  const debug = new URLSearchParams(location.search).has('debug');
  /** ?dev=1: the builder's chrome — stat cells, the toolbar and its button. Visitors never see it. */
  const dev = new URLSearchParams(location.search).has('dev');
  const [games, setGames] = createSignal<GameMeta[]>([]);
  const [feedSource, setFeedSource] = createSignal<'database' | 'network' | 'bundled' | null>(null);
  // ?own=1 (e2e): only cards we serve ourselves. The memory budget is for what we ship; a
  // linked third-party site brings its own renderer and its own footprint.
  const ownOnly = new URLSearchParams(location.search).has('own');
  const shape = (list: GameMeta[]) => (ownOnly ? list.filter((g) => !g.page_url || !/^https?:\/\//i.test(g.page_url) || new URL(g.page_url).origin === location.origin) : list);
  /**
   * /feed?bench=1 for an admin: the feed is the bench (bench_feed(), oldest first) and the review
   * strip sits under it (src/components/ReviewStrip.tsx). Anyone else with ?bench=1 gets the
   * normal feed and no strip; bench_feed refuses them anyway (42501). The build with no backend
   * has no admins, so the e2e suite marks one with window.__blitzFixture = { admin: true }, which
   * only this build honours. The transport runs as ever: an admin is internal, so nothing votes.
   */
  const wantBench = new URLSearchParams(location.search).has('bench');
  const fixtureAdmin = !supabaseConfigured && (window as unknown as { __blitzFixture?: { admin?: boolean } }).__blitzFixture?.admin === true;
  const [bench, setBench] = createSignal<Map<string, BenchCard> | null>(null);
  const [reviewed, setReviewed] = createSignal<Record<string, ReviewOutcome>>({});
  const [benchSummary, setBenchSummary] = createSignal<BenchSummary | null>(null);
  const loadBenchSummary = () =>
    void statsApi.benchSummary().then((r) => setBenchSummary(r.ok ? r.rows : null));
  const [feed] = createResource(async () => {
    if (wantBench && (fixtureAdmin || (await accountApi.load()).isAdmin)) {
      const list = await benchFeed();
      if (list && list.length) {
        setBench(new Map(list.map((c) => [c.meta.id, c] as const)));
        setGames(list.map((c) => c.meta));
        setFeedSource(supabaseConfigured ? 'database' : 'bundled');
        loadBenchSummary();
        return { games: list.map((c) => c.meta), source: supabaseConfigured ? ('database' as const) : ('bundled' as const) };
      }
      if (list) queueMicrotask(() => showToast('The bench is empty: nothing is waiting for a look.', LONG_TOAST_MS));
    }
    const result = await fetchFeed();
    let list = result.games;
    // A share link to a card the feed's page does not carry — past its first 60, or on the
    // bench and opened by its maker — is fetched by its slug and put first, before the carousel
    // mounts, so the visit starts on it and entry_surface 'share' lands on the card that was
    // shared rather than on card 0. The bundled catalogue answers only when the feed itself did
    // not come from the database: in production its cards are hidden, and stay hidden.
    const shared = cardFromLocation();
    if (shared && !list.some((g) => g.id === shared)) {
      const meta = result.source === 'database' ? await cardBySlug(shared) : bySlug(shared);
      if (meta) list = [meta, ...list];
    }
    setGames(shape(list));
    setFeedSource(result.source);
    feedReady = true;
    queueMicrotask(putMyWaitingNext);
    return result;
  });
  /**
   * Reaching the end card re-reads the feed in the background; BLITZ there starts over on
   * whatever has arrived since, so the loop is also the refresh. Cards this tab added itself
   * that the fresh feed does not carry — a maker's own upload or link still waiting for a look —
   * are put back next after the first card, and a dropped .zip or .wasm stays at the tail
   * (src/lib/mine.ts).
   */
  let fresh: GameMeta[] | null = null;
  const refreshFeed = () => {
    if (feedSource() !== 'database') return; // the bundled and mock feeds do not change under us
    if (bench()) return; // the bench is reviewed as it was opened; reload the page for a fresh one
    void fetchFeed()
      .then((r) => {
        fresh = shape(r.games);
      })
      .catch(() => {
        fresh = null;
      });
  };
  const applyFresh = () => {
    const next = fresh;
    fresh = null;
    if (!next?.length) return;
    const have = games();
    // The same object is kept for a card already here (mergeFresh): the carousel's <For> keys
    // slides by identity, and a fresh object would rebuild that card's slide and its iframe.
    const merged = mergeFresh(have, next, addedHere);
    if (merged.length === have.length && merged.every((g, k) => g.id === have[k].id)) return;
    setGames(merged);
  };
  /**
   * The ids this tab put in itself: an upload, a link, a card of the maker's opened from their
   * profile, and their waiting cards put next on arrival. The loop keeps these (mergeFresh).
   */
  const addedHere = new Set<string>();
  /**
   * A maker's own cards still on the bench, put next on every visit (at most three, newest
   * first), once both the feed and the account have arrived. Not on the bench view, and not
   * for anyone without an account: they have no cards.
   */
  let feedReady = false;
  let waitingAsked = false;
  const putMyWaitingNext = () => {
    if (waitingAsked || !feedReady || bench() || feedSource() !== 'database' || !acct().isAccount) return;
    waitingAsked = true;
    void myCards().then((mine) => {
      const add = waitingToAdd(mine, games());
      if (!add.length) return;
      for (const g of add) addedHere.add(g.id);
      // After the card on screen, without moving to them: they are next, not now.
      const at = Math.min(games().length, index() + 1);
      setGames((list) => [...list.slice(0, at), ...add, ...list.slice(at)]);
    });
  };
  const [stats, setStats] = createSignal<LiveStats | null>(null);
  const [index, setIndex] = createSignal(0);
  const [devOpen, setDevOpen] = createSignal(dev);
  const [toast, setToast] = createSignal<string | null>(null);
  const [api, setApi] = createSignal<CarouselApi | null>(null);
  const [dragging, setDragging] = createSignal(false);
  const [linkOpen, setLinkOpen] = createSignal(false);
  /** The tab the + sheet opens on when something asked for one (?make=1); otherwise its last. */
  const [linkMode, setLinkMode] = createSignal<SheetMode | undefined>();
  const closeLink = () => {
    setLinkOpen(false);
    setLinkMode(undefined);
  };
  // Comments: which card's are open, and the live counts the rail shows.
  const [commentsFor, setCommentsFor] = createSignal<GameMeta | null>(null);
  const [commentCounts, setCommentCounts] = createSignal<Record<string, number>>({});
  // Likes: kept on the profile (the server's list once the session is known), the heart
  // follows it. A like is a bookmark, never a vote. Taps that are still on their way to the
  // server are kept apart, so a list that arrives late cannot undo a heart just tapped.
  const [likedIds, setLikedIds] = createSignal<Set<string>>(new Set());
  const pendingLikes = new Map<string, boolean>();
  const loadLikes = () =>
    void myLikes().then((list) => {
      if (!list) return; // could not read it: keep what this tab knows
      const n = new Set(list.map((g) => g.id));
      for (const [id, on] of pendingLikes) {
        if (on) n.add(id);
        else n.delete(id);
      }
      setLikedIds(n);
    });
  const flipLike = (id: string, on: boolean) =>
    setLikedIds((s) => {
      const n = new Set(s);
      if (on) n.add(id);
      else n.delete(id);
      return n;
    });
  const onLike = async (meta: GameMeta, v: boolean) => {
    flipLike(meta.id, v);
    pendingLikes.set(meta.id, v);
    const r = v ? await likeCard(meta.id) : await unlikeCard(meta.id);
    if (pendingLikes.get(meta.id) === v) pendingLikes.delete(meta.id);
    if (!r.ok) {
      flipLike(meta.id, !v);
      if (r.reason) showToast(r.reason);
    }
  };
  /**
   * A card of this person's was taken down: it leaves this feed too, through the carousel so
   * the cursor stays on the card they were looking at, and it cannot come back from a feed
   * read that predates the takedown.
   */
  const dropCard = (id: string) => {
    if (fresh) fresh = fresh.filter((g) => g.id !== id);
    const a = api();
    if (a) a.removeGame(id);
    else setGames((list) => list.filter((g) => g.id !== id));
  };
  const onRemoved = (id: string) => {
    dropCard(id);
    void accountApi.load().then(setAcct);
  };
  /**
   * An admin takes a live card down from the feed itself: the eye button in the top bar opens
   * the review strip's five words, one tap calls card_set_status with 'hidden: <word>' (the same
   * door and the same log as the bench strip), and the card leaves this feed the way a maker's
   * own takedown does. Not on the bench view, which has the strip. The build with no backend
   * honours the e2e fixture's admin, as the strip does.
   */
  const canHide = () => !bench() && (acct().isAdmin || fixtureAdmin);
  const [hideOpen, setHideOpen] = createSignal(false);
  const [hideBusy, setHideBusy] = createSignal(false);
  const hideCurrent = async (word: HideReason) => {
    const g = games()[index()];
    if (!g || hideBusy()) return;
    setHideBusy(true);
    const r = await setCardStatus(g.id, 'hidden', hideReason(word));
    setHideBusy(false);
    setHideOpen(false);
    if (!r.ok) {
      showToast(r.reason);
      return;
    }
    showToast(`Hidden: ${word}`);
    dropCard(g.id);
  };
  // Who this is. Anonymous until the backend says otherwise; the sheet updates it in place.
  const [acct, setAcct] = createSignal<Account>(supabaseConfigured ? ANONYMOUS : NO_BACKEND);
  const [accountOpen, setAccountOpen] = createSignal(false);
  const [accountView, setAccountView] = createSignal<AccountView>('create');
  /**
   * Creating an account and signing in are pages of their own (/join, /signin): the door leaves
   * the feed, carrying this visit's attribution and the card it is on so finishing comes back to
   * it. The profile is a page too (/you). Only "check your inbox" still opens here as a sheet.
   */
  const openAccount = (view: AccountView, why?: 'link') => {
    if (view === 'create' || view === 'signin') {
      const e = entryContext();
      location.assign(accountPageHref(view, { via: e.entry_source, r: e.referrer_user_id, back: location.pathname, why }));
      return;
    }
    // The profile is a page of its own too (27 Sep): /you, without the feed under it.
    if (view === 'profile') {
      location.assign('/you');
      return;
    }
    setAccountView(view);
    closeLink();
    setCommentsFor(null);
    setAccountOpen(true);
  };
  /** The view that fits who this is: profile for an account, confirm while an email is pending. */
  const accountEntry = (): AccountView => (acct().isAccount ? 'profile' : acct().pendingEmail ? 'confirm' : 'create');

  /** An account with a handle, on a build with a backend: a dropped file is published, not previewed. */
  const canPublish = () => supabaseConfigured && linkGate(acct()) === 'none';

  /**
   * Drop-to-publish. For an account with a handle, a photo, a video or an HTML page goes into
   * the bucket and comes back as a card under their handle, next in their feed and on the bench
   * for everyone else until a person has opened it on a phone — the frictionless path for
   * something a model just wrote. Otherwise (no backend, no account,
   * a .wasm or .zip) the file is tried locally: a blob: URL in this tab, not shareable, gone
   * on reload.
   */
  const acceptDropped = async (file: File) => {
    if (canPublish() && !('error' in classifyMedia(file))) {
      showToast('Uploading…');
      const r = await uploadMedia(file);
      if (r.ok) {
        addedHere.add(r.meta.id);
        api()?.insertGame(r.meta);
        showToast(addedCopy({ handle: acct().handle, developer: r.meta.developer, existing: r.existing, status: r.status }), LONG_TOAST_MS);
      } else if (r.code === 'account_required') openAccount('create');
      else if (r.code === 'handle_required') openAccount('profile');
      else showToast(r.reason);
      return;
    }
    try {
      const result = await importGameFile(file);
      api()?.insertGame(result.game);
      const notes = result.notes.length ? ` · ${result.notes.join(' · ')}` : '';
      setToast(`Added ${result.game.title} (${(result.bytes / 1024).toFixed(1)} KB)${notes}`);
    } catch (err) {
      setToast(`Could not add that file: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  let dragDepth = 0;
  const onDragEnter = (e: DragEvent) => {
    if (!e.dataTransfer?.types.includes('Files')) return;
    e.preventDefault();
    dragDepth++;
    setDragging(true);
  };
  const onDragOver = (e: DragEvent) => {
    if (e.dataTransfer?.types.includes('Files')) e.preventDefault();
  };
  // dragleave fires when crossing between child elements too, so count enters and leaves
  // rather than clearing on the first one.
  const onDragLeave = () => {
    dragDepth = Math.max(0, dragDepth - 1);
    if (dragDepth === 0) setDragging(false);
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    dragDepth = 0;
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) void acceptDropped(file);
  };
  const uid = userId();
  // Navigation start to the first running card of this visit; the transport stamps it as
  // boot_ms on the visit's first card session. performance.now() is already that interval.
  let bootAt: number | undefined;
  // The end card logs 'end' when it BECOMES active, not on every onIndex that finds it active
  // (a card removal that lands the cursor there fires onIndex twice).
  let wasEnd = false;
  // The transport stamps this onto every row so pre-auth rows join the actor later.
  const transport = new TelemetryTransport({
    clientMintId: uid,
    positionOf: (id) => {
      const i = games().findIndex((g) => g.id === id);
      return i >= 0 ? i : undefined;
    },
    bootMs: () => bootAt,
  });
  // Record how this session arrived before anything emits telemetry.
  captureEntry();
  configureUi({ position: () => index() });
  let toastTimer = 0;

  const showToast = (message: string, ms = 2600) => {
    setToast(message);
    clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => setToast(null), ms);
  };

  const insertGame = (meta: GameMeta, at: number) => {
    setGames((list) => [...list.slice(0, at), meta, ...list.slice(at)]);
  };

  onMount(() => {
    void registerServiceWorker();
    // The email-confirmation link lands here. ?signup / ?signin on /feed never reach this point
    // (src/index.tsx sends them to /join and /signin before the feed mounts); on a card's own
    // link they still lead to those pages.
    const q = new URLSearchParams(location.search);
    if (q.has('signup')) openAccount('create');
    else if (q.has('signin')) openAccount('signin');
    else if (q.has('make')) {
      // The makers' door (/feed?make=1): the + sheet on Make one, for everyone; the sheet logs
      // make_open, and the account door waits until they press Add.
      setLinkMode('make');
      setLinkOpen(true);
    }
    // From the landing page once signed in: straight to the profile, or to linking.
    const wantAccount = q.has('account');
    const wantLink = q.has('link');
    const outcome = authOutcomeFromHash(INITIAL_HASH);
    if (outcome) {
      // Whatever happened, the tokens or the error must not sit in the address bar.
      try {
        history.replaceState(history.state, '', location.pathname + location.search);
      } catch {
        /* history unavailable */
      }
      if (outcome.kind === 'error') openAccount('signin', 'link'); // the sign-in page says the link did not work
    }
    void accountApi.load().then((a) => {
      // A forgotten-password link that reached the feed (an old link, or the landing's forward
      // before it knew): the session is in place now, so choose the new password on /reset.
      if (outcome?.kind === 'recovery' && a.isAccount) {
        location.replace('/reset');
        return;
      }
      setAcct(a);
      loadLikes();
      putMyWaitingNext();
      if (outcome?.kind === 'confirmed' && a.isAccount) {
        showToast(a.handle ? `Confirmed. You are @${a.handle}. Link anything.` : 'Confirmed. Pick your handle.');
        if (!a.handle) openAccount('profile');
      } else if (a.isAccount && accountOpen() && accountView() === 'create') {
        openAccount('profile');
      } else if (wantAccount) {
        openAccount(a.isAccount ? 'profile' : a.pendingEmail ? 'confirm' : 'signin');
      } else if (wantLink) {
        // Back from /join's "Add what you made" with a Make one answer waiting: open on Make one,
        // which puts it back. Otherwise the sheet itself shows the account door if there is no account.
        if (hasMakeDraft()) setLinkMode('make');
        setLinkOpen(true);
      }
    });
    // Sign-in, sign-out, a token refresh that carries the confirmed claim: re-read who this is.
    // SIGNED_IN is deliberately not handled — load() itself signs in anonymously and would loop.
    const sub = supabase?.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT' || event === 'USER_UPDATED' || event === 'TOKEN_REFRESHED') {
        resetSession();
        void accountApi.load().then(setAcct);
        loadLikes();
      }
    });
    onCleanup(() => sub?.data.subscription.unsubscribe());
    const debugApi = {
      version: APP_VERSION,
      get api() {
        return api();
      },
      getState() {
        const a = api();
        const hosts = a?.hosts() ?? [];
        return {
          index: a?.index() ?? 0,
          count: a?.count() ?? 0,
          feedSource: feedSource(),
          crossOriginIsolated,
          sabAvailable: isSharedInputAvailable(),
          serviceWorker: serviceWorkerActive(),
          liveWorkers: hosts.filter((h) => h.workerAlive).length,
          hosts: hosts.map((h) => ({
            id: h.meta.id,
            serial: h.serial,
            state: h.state,
            workerAlive: h.workerAlive,
            inputMode: h.inputMode,
            ready: h.ready,
            error: h.lastError,
            stats: h.lastStats,
            dropped: h.droppedInputs,
          })),
          activeStats: stats(),
          transport: transport.getStatus(),
          telemetryLog: transport.log,
          uiLog: uiLog(),
        };
      },
      goTo: (i: number) => api()?.goTo(i, 'api'),
      // The build with no backend has no accounts, so the e2e suite stands one in to see the
      // doors a real visitor meets. Absent wherever there is a backend: there the server decides.
      // statusLog: what card_set_status was asked in this build, in order (the review strip's flips).
      ...(supabaseConfigured ? {} : { setAccount: (a: Partial<Account>) => setAcct({ ...acct(), ...a }), statusLog: MOCK_STATUS_LOG }),
      next: () => api()?.next(),
      prev: () => api()?.prev(),
      measureMemory,
      cacheStatus,
      transport,
    };
    (window as unknown as { __blitz: unknown }).__blitz = debugApi;
  });

  const activeInputMode = () => {
    // hosts() is a plain snapshot, not a signal: read stats() so this re-evaluates as the
    // active host comes alive (stats arrive at 4Hz), instead of freezing on the first null.
    stats();
    const a = api();
    const g = games()[index()];
    const host = a?.hosts().find((h) => h.meta.id === g?.id);
    return host?.inputMode ?? null;
  };

  return (
    <div
      class="app"
      classList={{ bench: !!bench() }}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <Show when={dragging()}>
        <div class="dropveil" aria-hidden="true">
          <div class="dropveil-card">
            <div class="dropveil-arrow">&darr;</div>
            <Show when={canPublish()} fallback={<p>Drop to try it here</p>}>
              <p>Drop to publish it</p>
            </Show>
            <Show
              when={canPublish()}
              fallback={<small>.html becomes a web card &middot; .wasm or .zip becomes a game &middot; this tab only — create an account to publish</small>}
            >
              <small>A photo, a video or an HTML page goes in under @{acct().handle}, next in your feed; everyone else sees it once it has been looked at on a phone &middot; .wasm or .zip is tried in this tab</small>
            </Show>
          </div>
        </div>
      </Show>
      <Show
        when={!unsupported}
        fallback={
          <div class="unsupported">
            <div>
              <h1>BLITZ</h1>
              <p>{unsupported}</p>
            </div>
          </div>
        }
      >
        <Show when={games().length} fallback={<div class="unsupported">{feed.loading ? <div class="spinner" /> : <p>No games in the feed.</p>}</div>}>
          <Carousel
            games={games()}
            initialGameId={cardFromLocation()}
            transport={transport}
            userId={uid}
            debug={debug}
            onStats={setStats}
            onIndex={(i) => {
              setIndex(i);
              // The address bar always names the current card, so copying it is a share.
              const g = games()[i];
              if (g) syncUrlToCard(g.id);
              else if (!wasEnd) {
                refreshFeed(); // the end card: see what has arrived before BLITZ starts over
                logUi('end', null, i);
              }
              wasEnd = !g;
            }}
            onInsert={insertGame}
            onRemove={(id) => setGames((list) => list.filter((g) => g.id !== id))}
            register={setApi}
            toast={showToast}
            onComments={(meta) => setCommentsFor(meta)}
            commentCounts={commentCounts()}
            liked={(id) => likedIds().has(id)}
            onLike={onLike}
            onLink={() => {
              logUi('link_open', null, index());
              setLinkOpen(true);
            }}
            onLoop={() => {
              logUi('loop', null, index());
              applyFresh();
            }}
            onFirstRunning={() => {
              if (bootAt === undefined) bootAt = performance.now();
            }}
          />
        </Show>

        <header class="top-bar">
          {/* The mark is the way to the landing page (the site root) — the one place that explains what this is. */}
          <a class="brand" data-ui href="/" data-testid="brand-link" aria-label="About Blitz">
            <img src="/icons/icon.svg" alt="" />
            BLITZ
          </a>
          <div class="progress-dots" aria-hidden="true">
            {games().map((_, i) => (
              <span classList={{ active: i === index() }} />
            ))}
          </div>
          <div class="top-actions">
            <button
              class="icon-btn who"
              classList={{ signed: acct().isAccount }}
              data-ui
              data-testid="account"
              aria-label={acct().handle ? `Your account, @${acct().handle}` : 'Account'}
              title={acct().handle ? `@${acct().handle}` : 'Create an account or sign in'}
              onClick={() => openAccount(accountEntry())}
            >
              <Show
                when={acct().handle}
                fallback={
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
                  </svg>
                }
              >
                {acct().handle![0].toUpperCase()}
              </Show>
            </button>
            <Show when={canHide()}>
              <button
                class="icon-btn hide"
                data-ui
                data-testid="hide-card"
                aria-label="Hide this card"
                aria-pressed={hideOpen()}
                title="Hide this card from the feed"
                onClick={() => setHideOpen((v) => !v)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 3l18 18" />
                  <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" />
                  <path d="M9.9 5.1A10.4 10.4 0 0 1 12 5c5 0 9 4.5 10 7-.4 1-1.3 2.4-2.6 3.6" />
                  <path d="M6.2 6.2C4 7.7 2.6 10 2 12c1 2.5 5 7 10 7 1.5 0 2.9-.4 4.1-1" />
                </svg>
              </button>
            </Show>
            <Show when={dev}>
              <button class="icon-btn" data-ui aria-label="Developer toolbar" aria-pressed={devOpen()} onClick={() => setDevOpen((v) => !v)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M4 7h16M4 12h10M4 17h6" />
                </svg>
              </button>
            </Show>
          </div>
        </header>

        <Show when={hideOpen() && canHide()}>
          <div class="hide-words" data-testid="hide-words" role="group" aria-label="Why hide this card">
            <For each={HIDE_REASONS}>
              {(w) => (
                <button class="btn" data-ui data-testid={`hide-${w}`} disabled={hideBusy()} onClick={() => void hideCurrent(w)}>
                  {w}
                </button>
              )}
            </For>
            <button class="btn quiet" data-ui onClick={() => setHideOpen(false)}>
              Cancel
            </button>
          </div>
        </Show>

        <Show when={bench()}>
          {(b) => (
            <ReviewStrip
              card={b().get(games()[index()]?.id ?? '') ?? null}
              outcome={reviewed()[games()[index()]?.id ?? ''] ?? null}
              summary={benchSummary()}
              left={[...b().keys()].filter((id) => !reviewed()[id]).length}
              onDone={(id, outcome) => {
                setReviewed((r) => ({ ...r, [id]: outcome }));
                loadBenchSummary();
                api()?.next();
              }}
            />
          )}
        </Show>

        <TelemetryDisplay
          stats={stats()}
          inputMode={activeInputMode()}
          onNext={() => api()?.next()}
          onBack={() => {
            logUi('back', games()[index()]?.id ?? null, index());
            api()?.prev();
          }}
          canBack={index() > 0}
          onLink={() => setLinkOpen(true)}
          dev={dev}
        />

        <AccountSheet
          open={accountOpen()}
          view={accountView()}
          account={acct()}
          onView={(v) => (v === 'create' || v === 'signin' ? openAccount(v) : setAccountView(v))}
          onClose={() => setAccountOpen(false)}
          onAccount={setAcct}
          toast={showToast}
          onGoTo={(id) => {
            const at = games().findIndex((g) => g.id === id);
            setAccountOpen(false);
            if (at >= 0) {
              api()?.goTo(at, 'api');
              return;
            }
            // Not in this feed (an older like, past the page feed() serves, or your own card
            // still on the bench): fetch it by its slug and put it next, the way a fresh link
            // lands, rather than reloading onto card 0.
            void cardBySlug(id).then((meta) => {
              if (!meta) {
                showToast('That card is not on Blitz any more.');
                return;
              }
              addedHere.add(meta.id);
              api()?.insertGame(meta);
            });
          }}
          onRemoved={onRemoved}
        />

        <CommentsSheet
          card={commentsFor()}
          account={acct()}
          onClose={() => setCommentsFor(null)}
          onNeedAccount={openAccount}
          onCount={(id, n) => setCommentCounts((m) => ({ ...m, [id]: n }))}
          toast={showToast}
        />

        <LinkSite
          open={linkOpen()}
          mode={linkMode()}
          account={acct()}
          onNeedAccount={openAccount}
          onClose={closeLink}
          onLinked={(meta, existing, status: CardStatus) => {
            closeLink();
            // Already in this feed (someone linked it before us): jump to it rather than
            // showing the same card twice.
            const have = games().findIndex((g) => g.id === meta.id);
            if (have >= 0) {
              api()?.goTo(have, 'api');
              showToast(existing ? 'Already on Blitz — here it is' : 'Added');
              return;
            }
            addedHere.add(meta.id);
            api()?.insertGame(meta);
            showToast(addedCopy({ handle: acct().handle, developer: meta.developer, existing, status }), LONG_TOAST_MS);
          }}
        />

        <Show when={devOpen()}>
          <DevToolbar api={api} transport={transport} activeIndex={index()} onClose={() => setDevOpen(false)} toast={showToast} />
        </Show>

        <Show when={toast()}>
          <div class="toast" role="status">
            {toast()}
          </div>
        </Show>
      </Show>
    </div>
  );
}
